import React, { useState, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { getSelectedSport, isCBB } from "../utils/helpers";
import PlayerChatbot from "./player_profiles/PlayerChatbot";
import { useTheme } from "../utils/theme";
import { getMBBRoasters } from "../utils/MBBApis";
import { useNavigate } from "react-router-dom";

const TeamRosterToggle = ({
  viewMode = "grid",
  searchQuery = "",
  teamFilter = false,
  setTeamFilter = () => { },
}) => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [selectedTeam, setSelectedTeam] = useState(null);
  // const [teamFilter, setTeamFilter] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeChatPlayer, setActiveChatPlayer] = useState(null);
  const navigate = useNavigate()

  // Load selected team from session storage on mount
  useEffect(() => {
    const savedTeam = sessionStorage.getItem("selectedTeam");
    if (savedTeam) {
      setSelectedTeam(JSON.parse(savedTeam));
    }
  }, []);

  useEffect(() => {
    if (searchQuery?.length) {
      applyFilters();
    }
  }, [searchQuery]);

  useEffect(() => {
    if (!searchQuery?.length) {
      fetchTeamPlayers(selectedTeam?.team_id);
    }
  }, [searchQuery]);

  const applyFilters = () => {
    let results = [...teamPlayers];

    // Apply search query
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      results = results?.filter(
        (player) =>
          (player?.Player || "").toLowerCase().includes(query) ||
          (player?.name || "").toLowerCase().includes(query)
      );
      setTeamPlayers(results);
    }
  };

  // Fetch team players when teamFilter becomes true
  useEffect(() => {
    if (teamFilter && selectedTeam) {
      console.log("Fetching team players for team ID:", selectedTeam?.team_id);
      fetchTeamPlayers(selectedTeam?.team_id);
    }
  }, [teamFilter, selectedTeam]);

  const fetchTeamPlayers = async (teamId) => {
    if (!teamId) return;

    setLoading(true);
    try {
      // Get the numeric team ID
      let apiTeamId =
        typeof teamId === "string" ? teamId.replace(/\D/g, "") : teamId;

      // Use the new API endpoint structure with sport type
      // const response = await apiClient.get(
      //   `${getSelectedSport()}/teams/${apiTeamId}/players`
      // );

      let response;
      if (getSelectedSport() == 'MBB') {
        // response = await apiClient.get(`https://slam-all-python-359065791766.us-central1.run.app/${getSelectedSport()}/tp-players/`, {
        //   params: {
        //     team: selectedTeam.name,
        //     skip: 0,
        //     schema: getSelectedSport(),
        //     limit: 100
        //   }
        // });
        // response = response?.data
        response = await getMBBRoasters(selectedTeam?.name)
      }
      else {
        response = await apiClient.get(`${getSelectedSport()}/teams/${apiTeamId}/players`);
      }
      // Process the players data based on the API response structure
      let playersData = [];

      if (response.data) {
        // The API now returns an array of players directly
        playersData = Array.isArray(response.data) ? response.data : [];

        // Map the API response to our expected format
        playersData = playersData.map(player => ({
          PlayerID: player.PlayerID || player.player_id,
          Player: player.Player || player.name || player?.player_name,
          Position: player.position || player.Position,
          Height: player.Height || player.height,
          Weight: player.Weight || player.weight,
          PlayerNo: player.PlayerNo || player.jersey_number,
          TeamAbbrev: player.TeamAbbrev || selectedTeam?.abbreviation,
          image: player.image || player.photo || player.ImageUrl,
          ERA: player.ERA || (player.stats?.pitching?.era?.toFixed(2)),
          AVG: player.AVG || (player.stats?.batting?.avg?.toFixed(3))
        }));
      }

      console.log(`Found ${playersData.length} players`);

      setTeamPlayers(playersData);

      // Publish an event so other components can use this data
      const teamPlayersEvent = new CustomEvent("teamPlayersLoaded", {
        detail: { players: playersData, team: selectedTeam },
      });
      window.dispatchEvent(teamPlayersEvent);
    } catch (error) {
      console.error("Error fetching team players:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedTeam) {
    return null; // Don't show anything if no team is selected
  }

  return viewMode === "grid" ? (
    <div className="my-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            setTeamFilter(!teamFilter);
            setActiveChatPlayer(null);
          }}
          className={`px-4 py-2 rounded-md flex items-center transition-colors ${teamFilter
            ? "themePrimary text-white"
            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          title={`${selectedTeam?.full_name || selectedTeam?.team_name}${selectedTeam?.conference ? ` - ${selectedTeam?.conference}` : ""
            }`}
        >
          {selectedTeam?.logo_url && (
            <img
              src={selectedTeam?.logo_url}
              alt={selectedTeam?.full_name || selectedTeam?.team_name}
              className="h-5 w-5 mr-2 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/SLAM_logo.png";
              }}
            />
          )}
          <span className="font-medium">
            {selectedTeam?.full_name || selectedTeam?.team_name}
          </span>
          <span className="ml-2 text-xs">
            {teamFilter ? "(Active)" : "(Filter)"}
          </span>
        </button>

        <a
          href={`/team-record/${selectedTeam?.teamID ||
            (selectedTeam?.team_id &&
              selectedTeam?.team_id.toString().replace(/\D/g, ""))
            }`}
          className={`px-4 py-2 rounded-md flex items-center themeSecondary text-white transition-colors ${"hover:bg-gray-600"
            }`}
          title="View team record and schedule"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v10H5V5z"
              clipRule="evenodd"
            />
            <path d="M7 7h6v2H7V7zm0 4h6v2H7v-2z" />
          </svg>
          <span className="font-medium">Team Record</span>
        </a>

        {loading && (
          <div className="flex items-center">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
            <span
              className={`ml-2 text-sm ${"text-gray-400"
                }`}
            >
              Loading...
            </span>
          </div>
        )}
      </div>

      {/* Display team players when filter is active */}
      {teamFilter && teamPlayers.length > 0 && (
        <div
          className={`mt-6 pt-6 border-t ${"border-gray-700"
            }`}
        >
          <div className="flex items-center mb-4">
            <div
              className="h-8 w-1 themeSecondary rounded mr-3"
              style={
                selectedTeam?.team_color
                  ? { backgroundColor: selectedTeam?.team_color }
                  : {}
              }
            ></div>
            <h2
              className={`text-2xl font-bold ${"text-gray-200"
                }`}
            >
              {selectedTeam?.full_name || selectedTeam?.team_name} Roster
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {teamPlayers.map((player, index) => {
              // Extract player data from the API response format
              const playerName =
                player.Player ||
                player.name ||
                `${player.first_name || ""} ${player.last_name || ""}`.trim();
              let playerPosition =
                player.Position ||
                player.position ||
                player.position_abbreviation ||
                "";
              const playerHeight = player.Height || player.height || "6'0\"";
              const playerWeight = player.Weight || player.weight || "185 lbs";
              const playerNo = player.PlayerNo || player.jersey_number || "";
              const teamAbbrev =
                player.TeamAbbrev ||
                selectedTeam?.abbreviation ||
                selectedTeam?.team_name;

              // If position is null or empty, try to guess based on player number
              if (!playerPosition) {
                const num = parseInt(playerNo);
                if (num < 10) playerPosition = "INF";
                else if (num < 20) playerPosition = "OF";
                else if (num < 30) playerPosition = "C";
                else playerPosition = "P";
              }

              // Calculate a SLAM score (mock)
              const slamScore = Math.floor(Math.random() * 30) + 60;

              // Determine NIL potential (mock)
              const nilLevel =
                slamScore > 85
                  ? "$$$$"
                  : slamScore > 75
                    ? "$$$"
                    : slamScore > 65
                      ? "$$"
                      : "$";

              // Get position styling
              const positionColors = getPositionColors(playerPosition);
              const positionBadgeColor = getPositionBadgeColor(playerPosition);

              return (
                <div
                  key={player.PlayerID || player.id || index}
                  className="bg-[#141414] overflow-hidden rounded-xl shadow-lg border border-gray-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
                // onClick={() =>
                // (window.location.href = `/player/${player.PlayerID || player.id || index
                //   }`)
                // }
                >
                  {/* Player image */}
                  <div className="h-48 bg-black relative">
                    <img
                      src={
                        player.image ||
                        player.photo ||
                        player.ImageUrl ||
                        `/SLAM_logo.png`
                      }
                      alt={playerName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/SLAM_logo.png";
                        e.target.classList.add("p-8");
                        e.target.style.objectFit = "contain";
                      }}
                    />
                    <div className="absolute top-0 left-0 p-2 z-10">
                      <div
                        className={`${positionBadgeColor} text-white text-sm font-bold px-2 py-1 rounded-md uppercase inline-block`}
                      >
                        {playerPosition}
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 p-3 bg-gradient-to-t from-black to-transparent w-full">
                      <h3 className="text-xl font-bold text-white">
                        {playerName}
                      </h3>
                      <p className="text-gray-300 text-xs">
                        {teamAbbrev} • {playerHeight} • {playerWeight}
                      </p>
                    </div>
                  </div>

                  {/* SLAM Score section */}
                  <div className="bg-gray-800 p-4 border-t border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="bg-yellow-400 rounded-full p-2 mr-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-900"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <span className="text-gray-400 font-medium">
                          SLAM SCORE
                        </span>
                      </div>
                      <span className="bg-yellow-400 text-gray-900 text-xl font-bold px-3 py-1 rounded">
                        {slamScore}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
                      <div
                        className="bg-yellow-400 h-2 rounded-full"
                        style={{ width: `${slamScore}%` }}
                      ></div>
                    </div>

                    {/* NIL potential */}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">NIL POTENTIAL</span>
                      <span className="text-yellow-400 font-bold">
                        {nilLevel}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2 mt-2 mb-4">
                      <div
                        className="bg-yellow-400 h-2 rounded-full"
                        style={{ width: (nilLevel?.length || 1) * 25 + "%" }}
                      ></div>
                    </div>

                    {/* Stats section */}

                    {/* <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-gray-800 p-3 rounded">
                        <div className="text-xs text-gray-400 mb-1">{isCBB() ? 'ERA 1' : 'Points Per Game (PPG)'} </div>
                        <div className="font-bold text-white">
                          {isCBB()
                            ? (player.ERA || (Math.random() * 5 + 1).toFixed(2))
                            : (player.ERA || (Math.random() * 25 + 5).toFixed(1))}
                        </div>
                      </div>
                      <div className="bg-gray-800 p-3 rounded">
                        <div className="text-xs text-gray-400 mb-1">{isCBB() ? 'BATTING AVG 1' : 'Rebounds Per Game (RPG)'}</div>
                        <div className="font-bold text-white">
                          {isCBB()
                            ? (player.AVG || (Math.random() * 0.3 + 0.2).toFixed(3))
                            : (player.AVG || (Math.random() * 10 + 2).toFixed(1))}
                          {isCBB() ? ' AVG' : ''}
                        </div>
                      </div>
                    </div> */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-gray-800 p-3 rounded">
                        <div className="text-xs text-gray-400 mb-1">
                          {isCBB() ? "Position" : "Position"}{" "}
                        </div>
                        <div className="font-bold text-white">
                          {isCBB()
                            ? player.Position || "-"
                            : player.Position || "-"}
                        </div>
                      </div>
                      <div className="bg-gray-800 p-3 rounded">
                        <div className="text-xs text-gray-400 mb-1">
                          {isCBB() ? "Height" : "Height"}
                        </div>
                        <div className="font-bold text-white">
                          {isCBB()
                            ? player.Height || "-"
                            : player.Height || "-"}
                          {isCBB() ? "" : ""}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 divide-x divide-gray-700 border-t border-gray-800">
                    <button
                      onClick={() => {
                        const playerId = player.PlayerID || player.id || index;
                        const path =
                          getSelectedSport() === 'MBB'
                            ? `/team-player-profile/${playerName}`
                            : `/player/${playerId}`;
                        // Replace with your router navigation logic
                        navigate(path, { state: { playerData: player } });
                      }}
                      className="themeSecondary hoverintoGray text-gray-900 py-3 px-4 text-center font-medium"
                    >
                      View Full Profile
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveChatPlayer(`${player?.PlayerID}`);
                      }}
                      className="themeSecondary  text-gray-900 py-3 px-4 text-center font-medium"
                    >
                      Chat with Roster
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {teamFilter && teamPlayers.length === 0 && !loading && (
        <div
          className={`mt-6 pt-6 border-t ${"border-gray-700"
            }`}
        >
          <div className="flex items-center mb-4">
            <div
              className="h-8 w-1 themeSecondary rounded mr-3"
              style={
                selectedTeam?.team_color
                  ? { backgroundColor: selectedTeam?.team_color }
                  : {}
              }
            ></div>
            <h2
              className={`text-2xl font-bold ${"text-gray-200"
                }`}
            >
              {selectedTeam?.full_name || selectedTeam?.team_name} Roster
            </h2>
          </div>
          <div
            className={`bg-gray-100 rounded-lg p-6 text-center ${"bg-gray-800"
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-12 w-12 mx-auto ${"text-gray-500"
                } mb-4`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p
              className={`text-lg font-medium ${"text-gray-200"
                }`}
            >
              No player data available
            </p>
            <p
              className={`${"text-gray-400"
                } mt-2`}
            >
              We couldn't find any players for this team.
            </p>
          </div>
        </div>
      )}
      {activeChatPlayer && (
        <PlayerChatbot
          key={activeChatPlayer}
          setActiveChatPlayer={setActiveChatPlayer}
          name={activeChatPlayer}
          defaultOpened={true}
        />
      )}
    </div>
  ) : (
    <div className="my-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            setTeamFilter(!teamFilter);
            setActiveChatPlayer(null);
          }}
          className={`px-4 py-2 rounded-md flex items-center transition-colors ${teamFilter
            ? "themePrimary text-white"
            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          title={`${selectedTeam?.full_name || selectedTeam?.team_name}${selectedTeam?.conference ? ` - ${selectedTeam?.conference}` : ""
            }`}
        >
          {selectedTeam?.logo_url && (
            <img
              src={selectedTeam?.logo_url}
              alt={selectedTeam?.full_name || selectedTeam?.team_name}
              className="h-5 w-5 mr-2 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/SLAM_logo.png";
              }}
            />
          )}
          <span className="font-medium">
            {selectedTeam?.full_name || selectedTeam?.team_name}
          </span>
          <span className="ml-2 text-xs">
            {teamFilter ? "(Active)" : "(Filter)"}
          </span>
        </button>

        <a
          href={`/team-record/${selectedTeam?.teamID ||
            (selectedTeam?.team_id &&
              selectedTeam?.team_id.toString().replace(/\D/g, ""))
            }`}
          className={`px-4 py-2 rounded-md flex items-center themeSecondary text-white transition-colors ${"hover:bg-gray-600"
            }`}
          title="View team record and schedule"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v10H5V5z"
              clipRule="evenodd"
            />
            <path d="M7 7h6v2H7V7zm0 4h6v2H7v-2z" />
          </svg>
          <span className="font-medium">Team Record</span>
        </a>

        {loading && (
          <div className="flex items-center">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
            <span
              className={`ml-2 text-sm ${"text-gray-400"
                }`}
            >
              Loading...
            </span>
          </div>
        )}
      </div>

      {/* Display team players when filter is active */}
      {teamFilter && teamPlayers.length > 0 && (
        <div
          className={`mt-6 pt-6 border-t ${"border-gray-700"
            }`}
        >
          <div className="flex items-center mb-4">
            <div
              className="h-8 w-1 themeSecondary rounded mr-3"
              style={
                selectedTeam?.team_color
                  ? { backgroundColor: selectedTeam?.team_color }
                  : {}
              }
            ></div>
            <h2
              className={`text-2xl font-bold ${"text-gray-200"
                }`}
            >
              {selectedTeam?.full_name || selectedTeam?.team_name} Roster
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={`${"bg-gray-800"}`}>
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Player
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Position
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Height/Weight
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    SLAM Score
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    NIL Potential
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody
                className={`divide-y divide-gray-200 dark:divide-gray-700 ${"bg-[#0a0a0a]"
                  }`}
              >
                {teamPlayers.map((player, index) => {
                  const playerName =
                    player.Player ||
                    player.name ||
                    `${player.first_name || ""} ${player.last_name || ""
                      }`.trim();
                  let playerPosition =
                    player.Position ||
                    player.position ||
                    player.position_abbreviation ||
                    "";
                  const playerHeight =
                    player.Height || player.height || "-";
                  const playerWeight =
                    player.Weight || player.weight || "-";
                  const playerNo =
                    player.PlayerNo || player.jersey_number || "";
                  const slamScore = Math.floor(Math.random() * 30) + 60;
                  const nilLevel =
                    slamScore > 85
                      ? "$$$$"
                      : slamScore > 75
                        ? "$$$"
                        : slamScore > 65
                          ? "$$"
                          : "$";
                  const positionBadgeColor = getPositionBadgeColor(playerPosition);
                  if (!playerPosition) {
                    playerPosition = "-";
                  }

                  return (
                    <tr
                      key={player.PlayerID || player.id || index}
                      className={`${"hover:bg-gray-800"
                        }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              src={
                                player.image ||
                                player.photo ||
                                player.ImageUrl ||
                                `/SLAM_logo.png`
                              }
                              alt={playerName}
                              className="h-10 w-10 rounded-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/SLAM_logo.png";
                                e.target.classList.add("p-2");
                              }}
                            />
                          </div>
                          <div className="ml-4">
                            <div
                              className={`text-sm font-medium ${"text-white"
                                }`}
                            >
                              {playerName}
                            </div>
                            <div className="text-sm text-gray-500">
                              #{playerNo}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`${positionBadgeColor} text-white text-xs font-bold px-2 py-1 rounded-md uppercase`}
                        >
                          {playerPosition}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {playerHeight} / {playerWeight}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span
                            className={`mr-2 font-bold ${slamScore > 80
                              ? "text-yellow-400"
                              : "text-gray-300"
                              }`}
                          >
                            {slamScore}
                          </span>
                          <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-yellow-400 h-2 rounded-full"
                              style={{ width: `${slamScore}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span
                            className={`mr-2 font-bold ${nilLevel.length > 2
                              ? "text-yellow-400"
                              : "text-gray-300"
                              }`}
                          >
                            {nilLevel}
                          </span>
                          <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-yellow-400 h-2 rounded-full"
                              style={{
                                width: (nilLevel?.length || 1) * 25 + "%",
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <a
                            onClick={() => {
                              const playerId = player.PlayerID || player.id || index;
                              const path =
                                getSelectedSport() === 'MBB'
                                  ? `/team-player-profile/${playerName}`
                                  : `/player/${playerId}`;
                              // Replace with your router navigation logic
                              navigate(path, { state: { playerData: player } });
                            }}
                            // href={getSelectedSport() == 'MBB' ? `/team-player-profile/${player.PlayerID || player.id || index}` : `/player/${player.PlayerID || player.id || index}`}
                            className={`cursor-pointer px-3 py-1 rounded-md text-sm font-medium ${"themeSecondary text-gray-900 hover:bg-yellow-500"
                              }`}
                          >
                            Profile
                          </a>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveChatPlayer(`${player?.PlayerID}`);
                            }}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${"themeSecondary text-gray-900 hover:bg-yellow-500"
                              }`}
                          >
                            Chat
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {teamFilter && teamPlayers.length === 0 && !loading && (
        <div
          className={`mt-6 pt-6 border-t ${"border-gray-700"
            }`}
        >
          <div className="flex items-center mb-4">
            <div
              className="h-8 w-1 themeSecondary rounded mr-3"
              style={
                selectedTeam?.team_color
                  ? { backgroundColor: selectedTeam?.team_color }
                  : {}
              }
            ></div>
            <h2
              className={`text-2xl font-bold ${"text-gray-200"
                }`}
            >
              {selectedTeam?.full_name || selectedTeam?.team_name} Roster
            </h2>
          </div>
          <div
            className={`bg-gray-100 rounded-lg p-6 text-center ${"bg-gray-800"
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-12 w-12 mx-auto ${"text-gray-500"
                } mb-4`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p
              className={`text-lg font-medium ${"text-gray-200"
                }`}
            >
              No player data available
            </p>
            <p
              className={`${"text-gray-400"
                } mt-2`}
            >
              We couldn't find any players for this team.
            </p>
          </div>
        </div>
      )}
      {activeChatPlayer && (
        <PlayerChatbot
          key={activeChatPlayer}
          setActiveChatPlayer={setActiveChatPlayer}
          name={activeChatPlayer}
          defaultOpened={true}
        />
      )}
    </div>
  );

  // Helper functions for styling based on position
  function getPositionColors(position) {
    if (!position) return "from-blue-600 to-indigo-700";

    position = position.toLowerCase();
    if (
      position.includes("p") ||
      position.includes("rh") ||
      position.includes("lh")
    ) {
      return "from-blue-600 to-indigo-700";
    } else if (position.includes("c")) {
      return "from-amber-500 to-orange-600";
    } else if (
      position.includes("ss") ||
      position.includes("2b") ||
      position.includes("3b") ||
      position.includes("1b")
    ) {
      return "from-red-500 to-pink-600";
    } else if (
      position.includes("of") ||
      position.includes("lf") ||
      position.includes("cf") ||
      position.includes("rf")
    ) {
      return "from-green-600 to-teal-500";
    } else {
      return "from-purple-600 to-indigo-600";
    }
  }

  function getPositionBadgeColor(position) {
    if (!position) return "bg-indigo-600";

    position = position.toLowerCase();
    if (
      position.includes("p") ||
      position.includes("rh") ||
      position.includes("lh")
    ) {
      return "bg-indigo-600";
    } else if (position.includes("c")) {
      return "bg-amber-600";
    } else if (
      position.includes("ss") ||
      position.includes("2b") ||
      position.includes("3b") ||
      position.includes("1b")
    ) {
      return "bg-red-600";
    } else if (
      position.includes("of") ||
      position.includes("lf") ||
      position.includes("cf") ||
      position.includes("rf")
    ) {
      return "themePrimary";
    } else {
      return "bg-purple-600";
    }
  }
};

export default TeamRosterToggle;